import { useState } from 'react'
import { fbt } from 'fbtee'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { AudiencePicker } from './AudiencePicker'
import { useAudienceSelection } from '../hooks/useAudienceSelection'
import { ProviderIcon } from './MediaLinkCard'
import { detectMedia } from '../lib/media'
import type { CreateSetPostInput } from '../hooks/useSetPosts'
import type { Group } from '../types'

interface Props {
  groups: Group[]
  isGuest: boolean
  onRequireAuth: () => void
  onSubmit: (input: Omit<CreateSetPostInput, 'setId'>) => Promise<void>
}

export function SetPostComposer({ groups, isGuest, onRequireAuth, onSubmit }: Props) {
  const [text, setText] = useState('')
  const [mediaUrl, setMediaUrl] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // All channels selected (the default) means "everyone"; a strict subset restricts the post.
  const audience = useAudienceSelection(groups)
  const trimmedUrl = mediaUrl.trim()
  const detected = trimmedUrl ? detectMedia(trimmedUrl) : null
  const canSubmit = (text.trim() || trimmedUrl) && audience.count > 0 && !submitting

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (isGuest) {
      onRequireAuth()
      return
    }
    if (!canSubmit) return
    setSubmitting(true)
    await onSubmit({
      text: text.trim() || undefined,
      mediaUrl: trimmedUrl || undefined,
      ...audience.value,
    })
    setSubmitting(false)
    setText('')
    setMediaUrl('')
  }

  const audienceLabel = audience.isEveryone
    ? fbt('Everyone', 'Audience picker label when a set post is visible to all your groups and friends')
    : fbt(
        fbt.plural('channel', audience.count, { showCount: 'yes', many: 'channels' }),
        'Audience picker label showing how many groups/friends a restricted post targets',
      )

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <Textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={fbt('Hype this set, drop a comment…', 'Placeholder for the set feed comment box')}
        rows={2}
        className="resize-none"
      />
      <Input
        value={mediaUrl}
        onChange={e => setMediaUrl(e.target.value)}
        placeholder={fbt('Paste a YouTube / SoundCloud link', 'Placeholder for the set feed media-link input')}
        inputMode="url"
      />
      {detected && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <ProviderIcon provider={detected.provider} className="size-3.5" />
          <span className="capitalize">{detected.provider}</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <AudiencePicker
          groups={groups}
          selection={audience}
          triggerLabel={audienceLabel}
          header={<fbt desc="Header of the audience picker popover for a set post">Who can see this?</fbt>}
          friendsLabel={fbt('Friends', 'Audience option to share a set post with your friends')}
        />
        <Button type="submit" size="sm" disabled={!isGuest && !canSubmit}>
          {submitting
            ? fbt('Posting…', 'Button label while a set feed post is being submitted')
            : fbt('Post', 'Button label to submit a set feed post')}
        </Button>
      </div>
    </form>
  )
}
